import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import { JobWidgetView } from '@/features/jobs/job-widget/components/JobWidgetView'
import { JobWidgetHeader } from '@/features/jobs/job-widget/components/JobWidgetHeader'
import { JobWidgetDeadline } from '@/features/jobs/job-widget/components/JobWidgetDeadline'
import { JobWidgetStatus } from '@/features/jobs/job-widget/components/JobWidgetStatus'
import { Button } from '@/components/Button'
import { LabelChip } from '@/components/Chip'
import { links } from '@/utils/link'
import { progressionStepDescriptors } from '@/features/jobs/taxes/progression/progression-content'
import { useIsSkeleton } from '@/components/Skeleton'
import type { ConsultationStatus } from '@/features/jobs/consultations/Consultation'
import type { TimeRange } from '@/infra/time-range'
import { useProvideSupportWidgetHidden } from '@/features/support/components/ChatSupportSpaceManager'
import { Id } from '@/infra/id'
export default observer(function JobWidgetViewContainer() {
  const { internal } = useFeatures(({ jobs }) => ({
    internal: jobs.internal(),
  }))

  const vm = internal.jobWidgetVM
  const isSkeleton = useIsSkeleton(vm.isLoading)
  useActivation(vm)
  useProvideSupportWidgetHidden(true)

  const job = vm.jobWidget

  const { title: progressionTitle, description: progressionDescription } =
    progressionStepDescriptors[job.progressionStep]

  if (isSkeleton) {
    return (
      <JobWidgetView
        header={<JobWidgetHeader isSkeleton={isSkeleton} title="" />}
        button={
          <Button isSkeleton={isSkeleton} to="" label="" target="_blank" />
        }
      >
        <JobWidgetStatus
          isSkeleton={isSkeleton}
          title={job.name}
          icon="job-progression"
        />
      </JobWidgetView>
    )
  }

  const formatConsultationMessage = (
    consultationStatus: ConsultationStatus,
    bookedTime: TimeRange | undefined,
  ) => {
    if (
      consultationStatus === 'PendingCall' &&
      bookedTime?.isSameDay(new Date())
    ) {
      return `Your consultation is today at ${bookedTime.toStartingHourString()}`
    }

    if (consultationStatus === 'PendingCall' && bookedTime) {
      return `Your upcoming consultation is on ${bookedTime.toStartingDayString()} at ${bookedTime.toStartingHourString()}`
    }
  }

  const bookedTime = formatConsultationMessage(
    job.consultationStatus,
    job.consultationBookedTime,
  )

  const renderButton = (label: string) => (
    <Button
      onPress={vm.openPortal}
      isPending={vm.openPortal.pending}
      label={label}
    />
  )

  if (job.id === Id.none || job.status === 'Unknown') {
    return (
      <JobWidgetView
        header={
          <JobWidgetHeader
            title="Are you ready to start filing with us?"
            subtitle="Fast, simple, and secure from start to finish. We guide you through every step so you can file with confidence."
          />
        }
        button={
          <Button
            to={links.startNewService()}
            target="_blank"
            label="Don't wait — start filing today"
          />
        }
      >
        <JobWidgetDeadline
          isSkeleton={isSkeleton}
          title="Tax deadline"
          subtitle="File now and avoid last minute stress."
        />
      </JobWidgetView>
    )
  }

  const viewJobButton = renderButton('View job')
  const underConstructionButton = renderButton("Don't wait--start filing today")
  const checkStatusButton = renderButton('Check status')

  switch (job.status) {
    case 'UnderConstruction':
      return job.intakeCompleted ? (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Final step ahead!"
              subtitle="You've done the hard work, payment is the last step, before starting your filing. Pay now."
            />
          }
          button={underConstructionButton}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip text="Checkout" />
        </JobWidgetView>
      ) : (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Finish answering your questions"
              subtitle="You're almost there! Just answer the last few questions to continue."
            />
          }
          button={underConstructionButton}
        >
          <JobWidgetDeadline
            isSkeleton={isSkeleton}
            title="Tax deadline"
            subtitle="File now and avoid last minute stress."
          />

          <LabelChip isSkeleton={isSkeleton} text="Under Construction" />
        </JobWidgetView>
      )
    case 'InfoGathering':
      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Complete the list of requested documents"
              subtitle="Enter your personal information and upload all the required documents."
            />
          }
          button={renderButton('Upload Files')}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip isSkeleton={isSkeleton} text="Info gathering" />
        </JobWidgetView>
      )
    case 'Unclaimed':
      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Matching in progress"
              subtitle="We're connecting you to a Pro who will best assist with your inquiry"
            />
          }
          button={checkStatusButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip isSkeleton={isSkeleton} text="Waiting pick up" />
        </JobWidgetView>
      )
    case 'Idle':
      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Your job it's Idle"
              subtitle="Your job has been sitting idle for a while"
            />
          }
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip isSkeleton={isSkeleton} text="Idle" />
        </JobWidgetView>
      )
    case 'Closed':
      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Your tax return has been completed"
              subtitle="You will continue to have access to review the details of your tax return."
            />
          }
          button={renderButton('Review final tax return')}
        >
          <JobWidgetStatus title={job.name} icon="select-document" />

          <LabelChip
            isSkeleton={isSkeleton}
            text="Completed"
            variant="success"
          />
        </JobWidgetView>
      )
    case 'OnHold': {
      switch (job.scopeChangeStatus) {
        case 'PendingAdminApproval':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title="Your Pro is requesting a scope change"
                  subtitle="Your pro has identified that some additional services are required, a Customer Success Representative is reviewing the changes so the job can continue."
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Representative reviewing job" variant="danger" />
            </JobWidgetView>
          )
        case 'PendingClientApproval':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title="Your Pro is requesting a scope change"
                  subtitle="Your Pro has identified that some additional services are required, please approve the changes so that the job can continue"
                />
              }
              button={renderButton('Approve or reject change')}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Scope change requested" variant="danger" />
            </JobWidgetView>
          )
      }

      switch (job.progressionStep) {
        case 'OpenItems':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="On Hold" variant="danger" />
            </JobWidgetView>
          )
        case 'DraftInReview':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton('Approve or reject draft')}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Draft in review" />
            </JobWidgetView>
          )
        case 'ExtensionRejected':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension accepted" />
            </JobWidgetView>
          )
        case 'ProgressionWaitingOnClient':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Action requierd" variant="danger" />
            </JobWidgetView>
          )
      }

      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Your job has been placed on hold"
              subtitle="A Customer Success Representative placed your job on hold. If you think this is a mistake, please reach out to Customer Success for assistance or chat with your Pro."
            />
          }
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="time" />

          <LabelChip text="On Hold" />
        </JobWidgetView>
      )
    }
    case 'Claimed': {
      switch (job.progressionStep) {
        case 'OpenItems':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              bookedTime={bookedTime}
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="On Hold" />
            </JobWidgetView>
          )
        case 'JobStarted':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton('Chat with the pro')}
            >
              <JobWidgetStatus title={job.name} icon="time" />

              <LabelChip text="Job started" />
            </JobWidgetView>
          )
        case 'DraftApproved':
          return (
            <JobWidgetView
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              bookedTime={bookedTime}
              button={renderButton('Sign Authorization')}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Draft approved" />
            </JobWidgetView>
          )
        case 'DraftRejected':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Changes requested" variant="danger" />
            </JobWidgetView>
          )
        case 'AuthorizationSigned':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Authorization received" />
            </JobWidgetView>
          )
        case 'ReturnFiled':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={renderButton('Review final tax return')}
            >
              <JobWidgetStatus title={job.name} icon="select-document" />

              <LabelChip text="Return field" />
            </JobWidgetView>
          )
        case 'ExtensionInProgress':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension in progress" />
            </JobWidgetView>
          )
        case 'ExtensionConfirmed':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={checkStatusButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Waiting for confirmation" />
            </JobWidgetView>
          )
        case 'ReadyToFile':
          return (
            <JobWidgetView
              bookedTime={bookedTime}
              header={
                <JobWidgetHeader
                  title={progressionTitle}
                  subtitle={progressionDescription}
                />
              }
              button={viewJobButton}
            >
              <JobWidgetStatus title={job.name} icon="update-job" />

              <LabelChip text="Extension completed" />
            </JobWidgetView>
          )
      }

      return (
        <JobWidgetView
          header={
            <JobWidgetHeader
              title="Well done! The job has just started"
              subtitle="Talk to your Pro to have more information regarding next steps"
            />
          }
          bookedTime={bookedTime}
          button={viewJobButton}
        >
          <JobWidgetStatus title={job.name} icon="job-progression" />

          <LabelChip text="Assigned pro" />
        </JobWidgetView>
      )
    }
  }
})
